import clsx from "clsx";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
    accountApplicationAtom,
    accountDisplayListAtom,
    labelingContextViewAtom,
    NormalizedAccount,
    selectedRowAtom,
} from "@/features/Labeling/state";
import { useCallback, useState } from "react";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { AccountList as AccountListComponent } from "../components/AccountList";
import SearchBar from "@/features/Navigation/components/SearchBar";
import { PlusIcon } from "@heroicons/react/24/outline";

export function AccountList() {
    const getAccountDisplayList = useAtomValue(accountDisplayListAtom);
    const [contextView, setContextView] = useAtom(labelingContextViewAtom);
    const [selectedRow, setSelectedRow] = useAtom(selectedRowAtom);
    const setAccountApplication = useSetAtom(accountApplicationAtom);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const isRowSelection =
        selectedRow && contextView?.type === "ACCOUNT_SELECTION_LIST";
    const description = isRowSelection
        ? "Select an account for this row"
        : "Select an account to edit";
    const applyRow = useCallback(
        (account: NormalizedAccount) => {
            if (selectedRow) {
                setAccountApplication({
                    rowHeader: selectedRow.rowIndex,
                    accountId: account.id,
                });
                setSelectedRow(null);
            }
        },
        [selectedRow, setAccountApplication, setSelectedRow],
    );

    const handleClose = useCallback(() => {
        setContextView(null);
        // setSelectedRow(null);
    }, [setContextView]);

    return (
        <div
            className={clsx(
                "absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className={clsx("mb-3 mt-6 px-6", "flex flex-col", "sticky")}>
                <div className="flex items-start justify-between">
                    <RightSidebarButton
                        onClick={handleClose}
                        useEscapeToClose={true}
                    />
                    {isRowSelection && (
                        <button
                            className="quiet -mt-1 p-0"
                            onClick={() =>
                                setContextView({
                                    type: "ACCOUNT_SELECTION_NEW",
                                })
                            }
                        >
                            New Account <PlusIcon className="size-4" />
                        </button>
                    )}
                </div>
                <h2 className="pageheading">
                    {selectedRow?.rowHeader || "List of Accounts"}
                </h2>
                <p className="body-s subdued mb-6">{description}</p>
                {isRowSelection &&
                    selectedRow?.account?.accountId &&
                    selectedRow?.account?.label && (
                        <AccountListComponent
                            selectedId={
                                selectedRow?.hasAppliedAccount
                                    ? selectedRow?.account?.accountId
                                    : undefined
                            }
                            accounts={[
                                {
                                    id: selectedRow?.account?.accountId,
                                    name: selectedRow?.account?.accountName,
                                    label: selectedRow?.account?.label,
                                    type: selectedRow?.account?.type,
                                    meta: selectedRow?.account?.meta,
                                },
                            ]}
                            recommendedIds={
                                !selectedRow?.hasAppliedAccount
                                    ? [selectedRow?.account?.accountId]
                                    : []
                            }
                            onAccountSelection={applyRow}
                        />
                    )}
                <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search accounts..."
                />
            </div>

            <div className="custom-scrollbar overflow-y-auto px-3 pr-6">
                <AccountListComponent
                    recommendedIds={
                        selectedRow?.account?.accountId
                            ? [selectedRow?.account?.accountId]
                            : []
                    }
                    accounts={getAccountDisplayList(
                        searchTerm,
                        !isRowSelection,
                    )}
                    onAccountSelection={(account) => {
                        if (isRowSelection) {
                            applyRow(account);
                        } else {
                            setContextView({
                                type: "ACCOUNT_EDIT",
                                initial: account,
                            });
                        }
                    }}
                />
            </div>
        </div>
    );
}
